import { useEffect, useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  Button,
  Center,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Spacer,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';

import { PaginationState, SortingState } from '@tanstack/react-table';

import {
  Company,
  CompanyStatus,
  CompanyType,
  DropdownOption,
  FilterData,
  Filters,
  MultiSelectionData,
  SearchNormalOutline,
  shadowStyles,
  AddCircleOutline,
} from '@aslanpay/portal-common-lib';

import ConnectCezanneWizardModal from 'app/components/Companies/ConnectCezanneWizard/ConnectCezanneWizardModal';
import CreateCompanyChoiceModal from 'app/components/Companies/CreateCompanyChoiceModal';
import CreateCompanyModal from 'app/components/Companies/CreateCompany/CreateCompanyModal';
import DeleteCompanyModal from 'app/components/Companies/DeleteCompany/DeleteCompanyModal';
import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';
import { Table } from 'app/components/UI/Table/Table';
import { CompanyActions } from 'app/components/UI/Table/Table.types';

import { CompanyStatusValues, CompanyTypesLabels } from 'app/constants/companies';
import { defaultPageSize } from 'app/constants/table';

import useDebounce from 'app/hooks/useDebounce';
import useFetchCompaniesPage from 'app/hooks/useFetchCompaniesPage';

import { getSelectedCompanyPath, getSelectedCompanyDashboardPath } from 'app/utils/routes';
import getCompanyColumns from 'app/utils/table/companies';

import type { Filter } from '@aslanpay/portal-common-lib';

const companyStatusOptions: DropdownOption[] = Object.entries(CompanyStatusValues).map(
  ([value, data]) => ({
    value,
    text: data.value,
  })
);

const companyTypeOptions: DropdownOption[] = Object.entries(CompanyTypesLabels).map(
  ([value, text]) => ({
    value,
    text,
  })
);

function Companies() {
  const navigate = useNavigate();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isCreateChoiceOpen,
    onOpen: onOpenCreateChoice,
    onClose: onCloseCreateChoice,
  } = useDisclosure();
  const {
    isOpen: isConnectCezanneOpen,
    onOpen: onOpenConnectCezanne,
    onClose: onCloseConnectCezanne,
  } = useDisclosure();
  const {
    isOpen: isDeleteCompanyOpen,
    onOpen: onOpenDeleteCompany,
    onClose: onCloseDeleteCompany,
  } = useDisclosure();

  const handleChooseManual = () => {
    onCloseCreateChoice();
    onOpen();
  };

  const handleChooseCezanne = () => {
    onCloseCreateChoice();
    onOpenConnectCezanne();
  };

  const handleBackToChoiceFromManual = () => {
    onClose();
    onOpenCreateChoice();
  };

  const handleBackToChoiceFromCezanne = () => {
    onCloseConnectCezanne();
    onOpenCreateChoice();
  };

  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = searchParams.get('page') ? Number(searchParams.get('page')) - 1 : 0;

  const [companyToDeleteId, setCompanyToDeleteId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('query') || '');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: currentPage,
    pageSize: defaultPageSize,
  });
  const [statusFilter, setStatusFilter] = useState<CompanyStatus[]>(
    searchParams.get('status') ? (searchParams.get('status')?.split(',') as CompanyStatus[]) : []
  );
  const [typeFilter, setTypeFilter] = useState<CompanyType[]>(
    searchParams.get('type') ? (searchParams.get('type')?.split(',') as CompanyType[]) : []
  );
  const [sorting, setSorting] = useState<SortingState>(() => [
    ...(searchParams.get('sortBy')
      ? [
          {
            id: searchParams.get('sortBy') as string,
            desc: searchParams.get('sort') === 'DESC',
          },
        ]
      : []),
  ]);
  const [activeFilter, setActiveFilter] = useState<string | undefined>();

  const filters: Filter[] = [
    {
      name: 'Company status',
      type: 'category',
      data: companyStatusOptions,
      selectedValues: statusFilter,
    },
    {
      name: 'Company type',
      type: 'category',
      data: companyTypeOptions,
      selectedValues: typeFilter,
    },
  ];

  const resetPagination = () => {
    setPagination({ pageIndex: 0, pageSize: defaultPageSize });
  };

  const debouncedTyping = useDebounce((value: string) => {
    setDebouncedSearchQuery(value);
    resetPagination();
  }, 500);

  const { data: companies, isLoading: isLoadingCompanies } = useFetchCompaniesPage({
    query: debouncedSearchQuery,
    status: statusFilter,
    type: typeFilter,
    sortBy: sorting[0]?.id,
    sort: !sorting[0] || sorting[0]?.desc ? 'DESC' : 'ASC',
    page: pageIndex,
    size: pageSize,
  });

  const selectCompany = (company: Company) => {
    navigate(getSelectedCompanyPath(company.id));
  };

  const editCompany = (company: Company) => {
    navigate(getSelectedCompanyDashboardPath(company.id));
  };

  const deleteCompany = (company: Company) => {
    setCompanyToDeleteId(company.id);
    onOpenDeleteCompany();
  };

  const onSearchInput = (value: string) => {
    setSearchQuery(value);
    debouncedTyping(value);
  };

  const handleStatusChange = (values: (string | number)[]) => {
    setStatusFilter(values as CompanyStatus[]);
    resetPagination();
  };

  const handleTypeChange = (values: (string | number)[]) => {
    setTypeFilter(values as CompanyType[]);
    resetPagination();
  };

  const handleFilterClick = (filterName: string) => {
    setActiveFilter(filterName);
  };

  const handleFilterChange = (filterName: string, newValues: string[]) => {
    switch (filterName) {
      case 'Search': {
        const value = newValues[0] || '';

        setSearchQuery(value);
        setDebouncedSearchQuery(value);
        resetPagination();
        break;
      }
      case 'Company status':
        handleStatusChange(newValues);
        break;
      case 'Company type':
        handleTypeChange(newValues);
        break;
      default:
        break;
    }
  };

  const formatFilterValue = (
    filterName: string,
    filterValue: string | number,
    filterData: FilterData
  ) => {
    switch (filterName) {
      case 'Company status':
      case 'Company type':
        return (
          (filterData as MultiSelectionData).find((option) => option.value === filterValue)?.text ||
          String(filterValue)
        );
      default:
        return String(filterValue);
    }
  };

  const actions: CompanyActions = {
    select: selectCompany,
    edit: editCompany,
    delete: deleteCompany,
  };

  const PAGE_SIZE_OPTIONS = [
    { value: 15, text: '15 rows / page' },
    { value: 25, text: '25 rows / page' },
    { value: 50, text: '50 rows / page' },
  ];

  useEffect(() => {
    setSearchParams(
      {
        page: `${pageIndex + 1}`,
        ...(searchQuery && { query: searchQuery }),
        ...(sorting[0] && {
          sortBy: sorting[0]?.id,
          sort: sorting[0]?.desc ? 'DESC' : 'ASC',
        }),
        ...(statusFilter.length > 0 && { status: statusFilter.join(',') }),
        ...(typeFilter.length > 0 && { type: typeFilter.join(',') }),
      },
      { replace: true }
    );
  }, [pageIndex, searchQuery, sorting, statusFilter, typeFilter, setSearchParams]);

  return (
    <AuthenticatedPageWrapper title="Companies">
      <Flex
        flexDir="column"
        flexGrow={1}
        px="30px"
        py="40px"
        boxShadow={shadowStyles.dropdownMenu}
        borderRadius="10px"
        gap="20px"
      >
        <HStack>
          <Text as="h2" textStyle="h2" color="brand.darkPurple">
            Company Selection
          </Text>
          <Spacer />
          <Button
            leftIcon={<AddCircleOutline boxSize="20px" />}
            size="sm"
            onClick={onOpenCreateChoice}
          >
            Create new
          </Button>
          <CreateCompanyChoiceModal
            isOpen={isCreateChoiceOpen}
            onClose={onCloseCreateChoice}
            onChooseManual={handleChooseManual}
            onChooseCezanne={handleChooseCezanne}
          />
          <CreateCompanyModal
            isOpen={isOpen}
            onClose={onClose}
            onBack={handleBackToChoiceFromManual}
          />
          <ConnectCezanneWizardModal
            isOpen={isConnectCezanneOpen}
            onClose={onCloseConnectCezanne}
            onBack={handleBackToChoiceFromCezanne}
          />
          <DeleteCompanyModal
            id={companyToDeleteId}
            isOpen={isDeleteCompanyOpen}
            onClose={onCloseDeleteCompany}
            onDelete={resetPagination}
          />
        </HStack>
        <Flex flexDir="row" gap="10px">
          <InputGroup
            maxW="210px"
            aria-disabled={!searchQuery.length && !companies?.list.length}
            color="brand.darkGray"
            _disabled={{ color: 'system.disabled' }}
          >
            <Input
              isDisabled={!searchQuery.length && !companies?.list.length}
              onChange={(event) => onSearchInput(event.target.value)}
              defaultValue={searchQuery}
              type="text"
              placeholder="Search"
            />
            <InputRightElement w="56px" h="full">
              <SearchNormalOutline boxSize="24px" />
            </InputRightElement>
          </InputGroup>
        </Flex>
        <Flex justify="space-between">
          <Text my="10px" as="h5" textStyle="h5SemiBold" color="brand.darkGray">
            Please select a company to manage. You can switch anytime.
          </Text>
        </Flex>
        <Filters
          activeFilter={activeFilter}
          displayFormatter={formatFilterValue}
          filters={filters}
          onFilterClose={() => setActiveFilter(undefined)}
          onFilterChange={handleFilterChange}
          onFilterClick={handleFilterClick}
        />
        {isLoadingCompanies ? (
          <Center width="full" height="full" flexGrow={1}>
            <Spinner color="brand.purple" size="xl" mb="50px" />
          </Center>
        ) : (
          <Table
            columns={getCompanyColumns(actions)}
            data={companies?.list || []}
            total={companies?.total || 0}
            pageNumber={companies?.pageNumber || pageIndex}
            pageSize={companies?.pageSize || pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPaginationChange={setPagination}
            sorting={sorting}
            onSortingChange={setSorting}
          />
        )}
      </Flex>
    </AuthenticatedPageWrapper>
  );
}

export default Companies;
